import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

const PlayerContext = createContext(null)

function normalizeEpisode(episode) {
  return {
    title: episode.title || 'Без названия',
    audio: episode.audio,
    cover: episode.cover || '/cover.jpg',
    duration: episode.duration || '',
    slug: episode.slug || '',
    season: episode.season || null,
    episode: episode.episode || null,
  }
}

export function PlayerProvider({ children }) {
  const audioRef = useRef(null)

  const [currentEpisode, setCurrentEpisode] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playRequest, setPlayRequest] = useState(0)

  const playEpisode = useCallback((episode) => {
    if (!episode?.audio) return

    setCurrentEpisode(normalizeEpisode(episode))
    setIsPlaying(true)
    setPlayRequest((value) => value + 1)
  }, [])

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current

    if (!audio || !currentEpisode?.audio) return

    if (audio.paused) {
      try {
        await audio.play()
        setIsPlaying(true)
      } catch {
        setIsPlaying(false)
      }
    } else {
      audio.pause()
      setIsPlaying(false)
    }
  }, [currentEpisode])

  const closePlayer = useCallback(() => {
    const audio = audioRef.current

    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }

    setCurrentEpisode(null)
    setIsPlaying(false)
  }, [])

  const value = useMemo(() => ({
    audioRef,
    currentEpisode,
    isPlaying,
    playRequest,
    playEpisode,
    togglePlay,
    closePlayer,
    setIsPlaying,
  }), [
    currentEpisode,
    isPlaying,
    playRequest,
    playEpisode,
    togglePlay,
    closePlayer,
  ])

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  )
}

export function usePlayer() {
  const context = useContext(PlayerContext)

  if (!context) {
    throw new Error('usePlayer must be used inside PlayerProvider')
  }

  return context
}